import throttle    from 'raf-throttle';
import addClass    from 'add-class';
import removeClass from 'remove-class';
import extend      from 'just-extend';
import isElement   from './is-element';
import {transitionProperty, transformProperty, transformCSSProperty} from './vendor-prefixes';

const defaultOptions = {
	tabCount: 5,
	defaultIndex: 0,
	duration: 300,
	timingFunction: 'ease-in-out',
};

/**
 *        _______________slide______________________________
 *       <  ##########                ##########            >
 *       <  #inactive#                #inactive#            >
 *       <  ########## ___viewport___ ##########            >
 *       <             | ########## |            ########## >
 *       <             | # frame  # | -anim--->  # frame  # >
 *       <             | ########## |            ########## >
 *       <_____________|____________|_______________________>
 */
class TabbedContentSwapper {

	constructor(element, options) {
		this.element = element;
		this.options = extend({}, defaultOptions, options);
		this.frames = [];
		this.activeIndex = this.options.defaultIndex;

		this.init();
	}

	init() {
		this.viewportClass = 'tabbed-content-swapper-viewport';
		this.frameSlideClass = 'tabbed-content-swapper-slide';

		this.viewport = document.createElement('div');
		this.viewport.style.position = 'relative';
		this.viewport.style.width = '100%';
		this.viewport.style.overflow = 'hidden';
		this.viewport.style[transitionProperty] = `height ${this.options.duration}ms ${this.options.timingFunction}`;
		addClass(this.viewport, this.viewportClass);

		this.frameSlide = document.createElement('div');
		this.frameSlide.style.position = 'relative';
		this.frameSlide.style.width = '100%';
		addClass(this.frameSlide, this.frameSlideClass);

		// Init frames
		for (let i = 0; i < this.options.tabCount; i++) {
			let frame = this.createFrame(i);
			this.frames.push(frame);
			this.frameSlide.appendChild(frame);
		}

		// Collect all the child nodes from element to give to the frame slide
		var defaultFrameChildren = [];

		for (let i = this.element.children.length - 1; i >= 0; i--) {
			let el = this.element.children[i];
			this.element.removeChild(el);
			defaultFrameChildren.unshift(el);
		}

		this.swapContentAt(this.activeIndex, defaultFrameChildren);
		this.applyActiveStyle(this.frames[this.activeIndex]);

		this.viewport.appendChild(this.frameSlide);
		this.element.appendChild(this.viewport);

		this.updateHeight();
		this.updateFrameSlide();

		// Only set this after the slide has been moved into its initial place
		setTimeout(() => {
			this.frameSlide.style[transitionProperty] = `${transformCSSProperty} ${this.options.duration}ms ${this.options.timingFunction}`;
		}, 10);

		window.addEventListener('resize', throttle((event) => {
			this.updateHeight();
		}));
	}

	createFrame(index) {
		const frame = document.createElement('div');

		frame.style.position = 'absolute';
		frame.style.top = 0;
		frame.style.left = 100 * index + '%';
		frame.style.width = '100%';
		this.applyInactiveStyle(frame);

		addClass(frame, 'tabbed-content-swapper-frame');

		return frame;
	}

	applyActiveStyle(frame) {
		// No transition and move it back to its normal position
		frame.style[transitionProperty] = 'none';
		frame.style[transformProperty] = 'translate3d(0, 0, 0)';
		addClass(frame, 'active-frame');
	}

	applyInactiveStyle(frame) {
		// Give it a delayed but instant transition
		frame.style[transitionProperty] = `${transformCSSProperty} 0ms ${this.options.duration}ms`;
		// And move it out of view
		frame.style[transformProperty] = 'translate3d(0, -100%, 0)';
		removeClass(frame, 'active-frame');
	}

	set height(height) {
		this.viewport.style.height = height + 'px';
	}

	get height() {
		return this.frames[this.activeIndex].scrollHeight;
	}

	updateHeight() {
		this.height = this.frames[this.activeIndex].scrollHeight;
	}

	updateFrameSlide() {
		var x = -100 * this.activeIndex + '%';
		this.frameSlide.style[transformProperty] = `translate3d(${x}, 0, 0)`;
	}

	setActiveIndex(index) {
		this.applyInactiveStyle(this.frames[this.activeIndex]);
		this.applyActiveStyle(this.frames[index]);

		this.activeIndex = index;

		this.updateFrameSlide();
		this.updateHeight();
	}

	swapContentAt(frameIndex, content) {
		var frame = this.frames[frameIndex];

		// Clear out old content
		var children = [].slice.call(frame.children); // Safari can't iterate over an HTMLCollection
		for (let element of children)
			frame.removeChild(element);

		// Add the new content
		if (isElement(content))
			frame.appendChild(content);
		else if (content.length !== undefined) {
			let children = [].slice.call(content);
			for (let element of children)
				frame.appendChild(element);
		}
		else if (typeof content === 'string')
			frame.innerHTML = content;
		else
			throw new Error('content is of an uknown type');
	}

	getFrameElementAt(frameIndex) {
		return this.frames[frameIndex];
	}

	static create() {
		return new TabbedContentSwapper(...arguments);
	}

}

export default TabbedContentSwapper;



// WEBPACK FOOTER //
// ./assets/scripts/content-swapper.js